import { useCallback, useEffect, useState } from "react";
import { Cloud, CloudUpload, CloudDownload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getSyncMeta,
  pullOsBundle,
  pushOsBundle,
  type SyncMeta,
} from "@/lib/prometheusOsSync";

export type OsSyncPanelProps = {
  className?: string;
  compact?: boolean;
};

export function OsSyncPanel({ className, compact }: OsSyncPanelProps) {
  const [meta, setMeta] = useState<SyncMeta>(() => getSyncMeta());
  const [busy, setBusy] = useState<"push" | "pull" | null>(null);

  const reload = useCallback(() => setMeta(getSyncMeta()), []);

  useEffect(() => {
    reload();
    const sync = () => reload();
    window.addEventListener("prometheus-os-sync-changed", sync);
    return () => window.removeEventListener("prometheus-os-sync-changed", sync);
  }, [reload]);

  const onPush = async () => {
    setBusy("push");
    try {
      const saved = await pushOsBundle();
      toast.success(`Subido a la nube · ${saved.updatedAt?.slice(0, 19) || "ok"}`);
      reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo subir");
    } finally {
      setBusy(null);
    }
  };

  const onPull = async () => {
    setBusy("pull");
    try {
      const result = await pullOsBundle({ force: true });
      if (!result.applied) {
        toast.message(
          result.reason === "empty"
            ? "Nube vacía — sube primero desde este dispositivo"
            : "Ya estabas al día"
        );
      } else {
        toast.success("Descargado · inventario, meta, universidad y proyectos");
      }
      reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo bajar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card/50 p-4 space-y-3 font-mono-tech",
        className
      )}
    >
      <div className="flex items-start gap-2">
        <Cloud className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-primary font-bold">
            Sync nube · Chat OS
          </p>
          {!compact ? (
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Sube/baja inventario, seguridad/meta 3D, quizzes Universidad, plan activo y
              proyectos locales vía FastAPI (Supabase si 017 está aplicado; si no, JSON en
              Railway). Los GLB quedan solo en este navegador (IndexedDB).
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void onPush()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-[11px] font-bold cursor-pointer hover:opacity-90 disabled:opacity-50"
        >
          {busy === "push" ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CloudUpload className="w-3.5 h-3.5" />
          )}
          Subir
        </button>
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void onPull()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary px-3 py-2 text-[11px] font-bold cursor-pointer hover:bg-primary/20 disabled:opacity-50"
        >
          {busy === "pull" ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CloudDownload className="w-3.5 h-3.5" />
          )}
          Bajar
        </button>
      </div>

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        Último push: {meta.lastPushAt?.slice(0, 19) || "—"} · pull:{" "}
        {meta.lastPullAt?.slice(0, 19) || "—"}
      </p>
    </div>
  );
}
